import { type FC, useEffect, useState } from 'react'
import { FormProvider, useForm, type UseFormProps } from 'react-hook-form'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'
import isEmail from 'validator/lib/isEmail'

import { Button, Input } from 'base-ui'

import {
  BaseHookFormErrorMessage,
  RadioInputGroupHookFormController,
  type RadioParams,
  useRunResetOnSubmitSuccessfull,
} from '../../../base-form-handling'
import {
  FriendlyCaptchaChallenge,
  useHookFormFriendlyCaptchaFieldControls,
} from '../../../friendly-captcha'
import { useApiSwrMediServiceContactRxVideoPostMutation } from '../../api/useApiSwrMediServiceContactRxVideoPostMutation'
import { type MediServiceVideoServiceFormFields } from '../../model/MediServiceVideoServiceFormFields'
import { rxGetCampaignCode } from '../../rxCampaignCode'
import {
  type FormSubmissionStatus,
  MediServiceFormSubmissionNotification,
} from '../MediServiceFormSubmissionNotification/MediServiceFormSubmissionNotification'

const onSubmit = async (
  data: MediServiceVideoServiceFormFields,
  submitMutation: ReturnType<
    typeof useApiSwrMediServiceContactRxVideoPostMutation
  >,
  setFormSubmissionStatus: (status: FormSubmissionStatus) => void,
): Promise<void> => {
  if (submitMutation.isMutating) {
    return
  }

  try {
    await submitMutation.trigger(data)
    setFormSubmissionStatus('success')
  } catch {
    setFormSubmissionStatus('error')
  }
}

const getTimeslotParams = (intl: IntlShape): RadioParams[] => [
  {
    dataQaId: 'chrx.further-info.video-service.schedule.asap',
    id: 'video-2',
    label: intl.formatMessage({
      id: 'chrx.further-info.video-service.schedule.asap',
    }),
    value: 'chrx.further-info.video-service.schedule.asap',
  },
  {
    dataQaId: 'chrx.further-info.video-service.schedule.08-00-to-11-30',
    id: 'video-3',
    label: intl.formatMessage({
      id: 'chrx.further-info.video-service.schedule.08-00-to-11-30',
    }),
    value: 'chrx.further-info.video-service.schedule.08-00-to-11-30',
  },
  {
    dataQaId: 'chrx.further-info.video-service.schedule.13-30-to-16-30',
    id: 'video-4',
    label: intl.formatMessage({
      id: 'chrx.further-info.video-service.schedule.13-30-to-16-30',
    }),
    value: 'chrx.further-info.video-service.schedule.13-30-to-16-30',
  },
]

const formParams: UseFormProps<MediServiceVideoServiceFormFields> = {
  defaultValues: {
    option: 'chrx.further-info.video-service.schedule.asap',
  },
  mode: 'onBlur',
}

// eslint-disable-next-line max-lines-per-function -- whatever could be moved out of the function, already has been
export const MediServiceVideoServiceForm: FC = () => {
  const formSubmitMutation = useApiSwrMediServiceContactRxVideoPostMutation()
  const [formSubmissionStatus, setFormSubmissionStatus] =
    useState<FormSubmissionStatus>(false)

  const formMethods = useForm<MediServiceVideoServiceFormFields>(formParams)
  const { control, formState, handleSubmit, register, reset, setValue, watch } =
    formMethods
  useEffect(() => {
    const campaignCode = rxGetCampaignCode()
    if (campaignCode) {
      setValue('campaignCode', campaignCode)
    }
  }, [setValue])
  const intl = useIntl()

  const timeslotParams: RadioParams[] = getTimeslotParams(intl)

  const { onCaptchaDone, watchFriendlyCaptchaChange } =
    useHookFormFriendlyCaptchaFieldControls<MediServiceVideoServiceFormFields>({
      fieldName: 'fcSolution',
      register,
      setValue,
      watch,
    })

  useRunResetOnSubmitSuccessfull(formState.isSubmitSuccessful, reset)

  /* eslint-disable @typescript-eslint/no-misused-promises -- the rule was disabled before a comment became required */
  return (
    <FormProvider {...formMethods}>
      <form
        id="video-service-form"
        noValidate
        onSubmit={handleSubmit(async (data) => {
          await onSubmit(data, formSubmitMutation, setFormSubmissionStatus)
        })}
      >
        <div className="flex flex-col items-start gap-4">
          <div className="flex flex-1 flex-col items-start gap-2.5 self-stretch">
            <Input
              {...register('email', {
                required: intl.formatMessage({
                  id: 'chrx.messages.validation.required',
                }),
                validate: (input) =>
                  isEmail(input) ||
                  intl.formatMessage(
                    {
                      id: 'chrx.messages.validation.email',
                    },
                    { emailValue: input },
                  ),
              })}
              aria-label={intl.formatMessage({
                id: 'chrx.further-info.video-service.email',
              })}
              className="self-stretch"
              data-qa-id="chrx.further-info.video-service.email"
              id="email"
              name="email"
              placeholder={`${intl.formatMessage({
                id: 'chrx.further-info.video-service.email',
              })}*`}
              type="email"
            />
            <BaseHookFormErrorMessage
              data-qa-id="chrx-video-form-email-error"
              name="email"
            />
          </div>
          <div className="flex flex-1 flex-col items-start gap-2.5 self-stretch">
            <Input
              {...register('subject', {
                required: intl.formatMessage({
                  id: 'chrx.messages.validation.required',
                }),
              })}
              aria-label={intl.formatMessage({
                id: 'chrx.further-info.video-service.subject-placeholder',
              })}
              className="h-[120px] self-stretch rounded-2xl"
              data-qa-id="chrx.further-info.video-service.subject-placeholder"
              id="subject"
              multiline
              name="subject"
              placeholder={`${intl.formatMessage({
                id: 'chrx.further-info.video-service.subject-placeholder',
              })}*`}
              slotProps={{ input: { className: 'self-stretch min-h-[120px]' } }}
            />
            <BaseHookFormErrorMessage
              data-qa-id="chrx-video-form-subject-error"
              name="subject"
            />
          </div>
          <RadioInputGroupHookFormController
            className="flex flex-col items-start gap-3 self-stretch p-0"
            /* @ts-expect-error need more generic type here */
            control={control}
            name="option"
            radios={timeslotParams}
          />
          <FriendlyCaptchaChallenge
            className="mb-3.5"
            doneCallback={onCaptchaDone}
            startMode="auto"
            whenThisPropChangesResetCaptcha={
              formSubmitMutation.data ?? formSubmitMutation.error
            }
          />
          <Button
            className="min-h-[48px] self-stretch"
            color="secondary"
            data-qa-id="chrx.further-info.video-service.submit-label"
            disabled={!watchFriendlyCaptchaChange}
            type="submit"
          >
            <FormattedMessage id="chrx.further-info.video-service.submit-label" />
          </Button>
          <MediServiceFormSubmissionNotification
            dataQaId={`chrx-phone-form-submission-${formSubmissionStatus}`}
            formSubmissionStatus={formSubmissionStatus}
          />
        </div>
      </form>
    </FormProvider>
  )
  /* eslint-enable @typescript-eslint/no-misused-promises */
}
